"use client";

import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import {
  IoArrowBack,
  IoCalendarOutline,
  IoCallOutline,
  IoCheckmarkCircle,
  IoFlameOutline,
  IoGlobeOutline,
  IoLockClosedOutline,
  IoRibbonOutline,
  IoShieldCheckmarkOutline,
  IoSparkles,
  IoStar,
  IoVideocamOutline,
} from "react-icons/io5";
import ConsultationAppFlow from "./ConsultationAppFlow";
import { normalizeTokenFromSearch } from "../lib/consultationClient";
import { trackEvent } from "../lib/posthog";
import type { Expert } from "../types/expert";

interface ExpertProfileViewProps {
  expert: Expert;
}

export default function ExpertProfileView({ expert }: ExpertProfileViewProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const token = normalizeTokenFromSearch(
    searchParams?.get("t") ?? null,
    searchParams?.get("id") ?? null
  );
  const debugStep = searchParams?.get("booking_step") || searchParams?.get("debug_step") || "";
  const autoStart = searchParams?.get("start") === "1" || Boolean(debugStep);
  const appFlowActive = Boolean(token || autoStart);

  useEffect(() => {
    if (appFlowActive) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [appFlowActive]);

  const updateSearch = (next: URLSearchParams) => {
    const searchString = next.toString() ? `?${next.toString()}` : "";
    router.push(`${pathname}${searchString}`);
  };

  const openQuiz = (source = "expert_page") => {
    trackEvent("consultation_form_started", {
      consultation_type: "general",
      cta_source: source,
      expert_name: expert.name,
      expert_slug: expert.slug,
    });
    const next = new URLSearchParams(searchParams?.toString() || "");
    if (!token) {
      next.set("start", "1");
    }
    updateSearch(next);
  };

  const closeQuiz = () => {
    const next = new URLSearchParams(searchParams?.toString() || "");
    next.delete("start");
    next.delete("t");
    next.delete("id");
    next.delete("cid");
    next.delete("rb");
    next.delete("booking_step");
    next.delete("debug_step");
    updateSearch(next);
  };

  if (appFlowActive) {
    return (
      <div className="fixed inset-0 z-[100000] bg-[#faf7f2] overflow-y-auto overflow-x-hidden">
        <ConsultationAppFlow
          mode="organic"
          initialToken={token}
          defaultConcernArea="Career"
          debugStep={debugStep}
          onExit={closeQuiz}
        />
      </div>
    );
  }

  const specialties = expert.specialties && expert.specialties.length > 0
    ? expert.specialties
    : [
        "Vedic Astrology",
        "Kundali & Dasha Analysis",
        "Career & Business",
        "Marriage & Relationship",
        "Vedic Remedies",
        "Muhurta Selection",
      ];

  const certifications = expert.certifications && expert.certifications.length > 0
    ? expert.certifications
    : ["Certified Jyotish Acharya", "Astrotalk Certified", "Verified Astrologer"];

  const highlights = expert.highlights && expert.highlights.length > 0
    ? expert.highlights
    : [
        "Specialist in career transitions and business turnaround strategies.",
        "Over 20+ years of clinical horoscope analysis across 30+ countries.",
        "Pinpoint Dasha analysis and practical, non-fear-based Vedic remedies.",
      ];

  const languages = expert.languages && expert.languages.length > 0
    ? expert.languages
    : ["Hindi", "English"];

  const testimonials = expert.testimonials && expert.testimonials.length > 0
    ? expert.testimonials
    : [];

  const paragraphs = (expert.full_bio || expert.bio_summary || "")
    .split("\n\n")
    .filter((p) => p.trim().length > 0);

  const price = Number(expert.consultation_price) || 15000;
  const originalPrice = Number(expert.original_price) || 25000;
  const formattedPrice = `₹${price.toLocaleString("en-IN")}`;
  const formattedOriginalPrice = originalPrice ? `₹${originalPrice.toLocaleString("en-IN")}` : null;
  const discountPercent = originalPrice > price
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-slate-900 selection:bg-amber-100 selection:text-amber-900 pb-28 md:pb-16">
      {/* Top Header & Breadcrumbs */}
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-amber-900/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link
            href="/consultations#experts"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-700 hover:text-amber-700 transition-colors"
          >
            <IoArrowBack size={18} />
            <span>Back to Consultations</span>
          </Link>

          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200/60 text-emerald-800 text-[11px] font-semibold">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Available for Live Call</span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        {/* Profile Card Hero */}
        <section className="bg-white rounded-3xl border border-amber-900/10 shadow-xl shadow-amber-950/[0.03] overflow-hidden">
          <div className="p-5 sm:p-8 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              
              {/* Image / Portrait Column */}
              <div className="lg:col-span-5 flex flex-col items-center">
                <div className="relative w-full max-w-sm mx-auto rounded-2xl overflow-hidden border-2 border-amber-400/30 shadow-lg bg-gradient-to-b from-amber-50 to-orange-50/40">
                  {expert.image_url ? (
                    <div className="relative w-full flex items-center justify-center p-2 sm:p-3">
                      <Image
                        src={expert.image_url}
                        alt={expert.name}
                        width={768}
                        height={1024}
                        priority
                        className="w-full h-auto max-h-[440px] sm:max-h-[500px] object-contain rounded-xl shadow-sm"
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-[3/4] max-h-[440px] flex flex-col items-center justify-center p-8 text-center bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-600/10">
                      <div className="w-28 h-28 rounded-full bg-gradient-to-br from-amber-500 to-[#be5833] text-white flex items-center justify-center text-4xl font-serif font-bold shadow-md shadow-amber-500/20 mb-4">
                        {expert.name
                          .split(" ")
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join("")}
                      </div>
                      <p className="text-xs uppercase tracking-widest font-bold text-amber-800">
                        AstroJi Master Astrologer
                      </p>
                    </div>
                  )}

                  {/* Verification Banner Below Photo */}
                  <div className="bg-gradient-to-r from-amber-700 via-[#be5833] to-amber-700 text-white px-4 py-2.5 text-center flex items-center justify-center gap-2 text-xs font-semibold tracking-wide">
                    <IoShieldCheckmarkOutline size={16} className="text-amber-200" />
                    <span>Verified AstroJi Master Panel</span>
                  </div>
                </div>

                {/* Badges on mobile/tablet */}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                  {certifications.map((cert, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 border border-amber-200/80 text-amber-900"
                    >
                      <IoSparkles size={13} className="text-amber-600" />
                      {cert}
                    </span>
                  ))}
                </div>
              </div>

              {/* Bio & Details Column */}
              <div className="lg:col-span-7 flex flex-col">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800">
                    <IoRibbonOutline size={14} />
                    Senior Astrologer
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                    <IoGlobeOutline size={13} />
                    {languages.join(", ")}
                  </span>
                </div>

                <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-slate-900 leading-tight">
                  {expert.name}
                </h1>

                <p className="text-sm sm:text-base font-semibold text-[#be5833] mt-1.5 leading-snug">
                  {expert.title || "Vedic Astrologer & Life Strategist"}
                </p>

                {/* 4-Stat Metric Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 my-6">
                  <div className="p-3 sm:p-3.5 rounded-2xl bg-amber-50/60 border border-amber-200/70 text-center">
                    <p className="text-lg sm:text-xl font-extrabold text-amber-950 font-serif">
                      {expert.experience_years || "15+ Years"}
                    </p>
                    <p className="text-[11px] sm:text-xs font-medium text-amber-800 uppercase tracking-wide mt-0.5">
                      Experience
                    </p>
                  </div>

                  <div className="p-3 sm:p-3.5 rounded-2xl bg-orange-50/60 border border-orange-200/70 text-center">
                    <p className="text-lg sm:text-xl font-extrabold text-orange-950 font-serif">
                      {expert.consultations_count || "35,000+"}
                    </p>
                    <p className="text-[11px] sm:text-xs font-medium text-orange-800 uppercase tracking-wide mt-0.5">
                      Consultations
                    </p>
                  </div>

                  <div className="p-3 sm:p-3.5 rounded-2xl bg-amber-50/60 border border-amber-200/70 text-center">
                    <p className="text-lg sm:text-xl font-extrabold text-amber-950 font-serif">
                      {expert.prediction_hours || "15,000+ Hrs"}
                    </p>
                    <p className="text-[11px] sm:text-xs font-medium text-amber-800 uppercase tracking-wide mt-0.5">
                      Prediction Time
                    </p>
                  </div>

                  <div className="p-3 sm:p-3.5 rounded-2xl bg-emerald-50/60 border border-emerald-200/70 text-center">
                    <div className="flex items-center justify-center gap-1 text-lg sm:text-xl font-extrabold text-emerald-950 font-serif">
                      <IoStar className="text-amber-500 fill-amber-500" size={16} />
                      <span>{expert.rating ? Number(expert.rating).toFixed(2) : "4.96"}</span>
                    </div>
                    <p className="text-[11px] sm:text-xs font-medium text-emerald-800 uppercase tracking-wide mt-0.5">
                      {expert.reviews_count ? `${expert.reviews_count} Reviews` : "Top Rated"}
                    </p>
                  </div>
                </div>

                {/* Short Bio Summary */}
                {expert.bio_summary && (
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-6 font-normal">
                    {expert.bio_summary}
                  </p>
                )}

                {/* Instant Book CTA Box (Desktop & Tablet) */}
                <div className="p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-amber-900 via-[#8a391c] to-[#be5833] text-white shadow-lg shadow-amber-950/20">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/15 text-[11px] font-bold uppercase tracking-wider text-amber-200 mb-1">
                        <IoVideocamOutline size={14} />
                        Private 1-on-1 Video Consultation
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl sm:text-3xl font-extrabold font-serif">{formattedPrice}</span>
                        {formattedOriginalPrice && (
                          <span className="text-xs text-amber-100 line-through">{formattedOriginalPrice}</span>
                        )}
                        {discountPercent && (
                          <span className="text-xs font-semibold text-emerald-300">{discountPercent}% OFF</span>
                        )}
                      </div>
                      <p className="text-xs text-amber-100/90 mt-0.5">
                        20-30 Minutes • Astrologer studies chart before session
                      </p>
                    </div>

                    <button
                      onClick={() => openQuiz("hero_box")}
                      className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-300 text-slate-950 font-bold text-sm shadow-md hover:brightness-105 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <IoCalendarOutline size={18} />
                      <span>Book Consultation Now</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-4 mt-4 border-t border-white/15 text-[11px] text-amber-100/80 text-center">
                    <span className="flex items-center justify-center gap-1">
                      <IoLockClosedOutline size={13} className="text-amber-300" />
                      100% Private
                    </span>
                    <span className="flex items-center justify-center gap-1">
                      <IoShieldCheckmarkOutline size={13} className="text-amber-300" />
                      Authentic Vedic
                    </span>
                    <span className="flex items-center justify-center gap-1">
                      <IoCallOutline size={13} className="text-amber-300" />
                      Instant Scheduling
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Credentials & Key Highlights */}
        {highlights.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg sm:text-xl font-serif font-bold text-slate-900 mb-4 flex items-center gap-2">
              <IoFlameOutline className="text-[#be5833]" size={22} />
              <span>Distinguished Honors & Credentials</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {highlights.map((highlight, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-2xl bg-white border border-amber-900/10 shadow-sm flex items-start gap-3.5"
                >
                  <div className="w-8 h-8 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <IoCheckmarkCircle size={18} className="text-amber-600" />
                  </div>
                  <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">
                    {highlight}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Detailed Biography Section */}
        {paragraphs.length > 0 && (
          <section className="mt-10 bg-white rounded-3xl border border-amber-900/10 p-6 sm:p-8 shadow-sm">
            <h2 className="text-lg sm:text-xl font-serif font-bold text-slate-900 mb-4">
              About {expert.name}
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-slate-700 leading-relaxed font-normal">
              {paragraphs.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </section>
        )}

        {/* Areas of Mastery / Specialties */}
        <section className="mt-10">
          <h2 className="text-lg sm:text-xl font-serif font-bold text-slate-900 mb-4 flex items-center gap-2">
            <IoSparkles className="text-amber-600" size={20} />
            <span>Consultation Areas & Specializations</span>
          </h2>

          <div className="flex flex-wrap gap-2.5">
            {specialties.map((specialty, idx) => (
              <div
                key={idx}
                className="px-4 py-2 rounded-xl bg-white border border-amber-900/10 text-slate-800 text-xs sm:text-sm font-semibold shadow-sm flex items-center gap-2 hover:border-amber-400/50 hover:bg-amber-50/50 transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span>{specialty}</span>
              </div>
            ))}
          </div>
        </section>

        {/* How The Consultation Works */}
        <section className="mt-10 bg-gradient-to-br from-orange-50/60 to-amber-50/40 rounded-3xl border border-amber-200/60 p-6 sm:p-8">
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="text-xs uppercase font-bold tracking-widest text-[#be5833]">
              Simple 4-Step Process
            </span>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900 mt-1">
              How Your Private Consultation Works
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-2xl bg-white/80 border border-amber-900/10 text-center flex flex-col items-center">
              <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-900 font-bold text-sm flex items-center justify-center mb-3">
                1
              </span>
              <h3 className="text-sm font-bold text-slate-900 mb-1">Share Birth Details</h3>
              <p className="text-xs text-slate-600">
                Provide your date, exact time, and place of birth for precise chart calculation.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-white/80 border border-amber-900/10 text-center flex flex-col items-center">
              <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-900 font-bold text-sm flex items-center justify-center mb-3">
                2
              </span>
              <h3 className="text-sm font-bold text-slate-900 mb-1">Astrologer Prepares</h3>
              <p className="text-xs text-slate-600">
                {expert.name} analyzes planetary dashas, transits, and yogas prior to your call.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-white/80 border border-amber-900/10 text-center flex flex-col items-center">
              <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-900 font-bold text-sm flex items-center justify-center mb-3">
                3
              </span>
              <h3 className="text-sm font-bold text-slate-900 mb-1">Live 1-on-1 Call</h3>
              <p className="text-xs text-slate-600">
                Join a 20-30 min private video session. Ask your deepest personal or professional queries.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-white/80 border border-amber-900/10 text-center flex flex-col items-center">
              <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-900 font-bold text-sm flex items-center justify-center mb-3">
                4
              </span>
              <h3 className="text-sm font-bold text-slate-900 mb-1">Practical Remedies</h3>
              <p className="text-xs text-slate-600">
                Receive practical, non-fear based Vedic remedies, timing advice, and action plan.
              </p>
            </div>
          </div>
        </section>

        {/* Verified Client Reviews */}
        {testimonials.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-serif font-bold text-slate-900 flex items-center gap-2">
                <IoStar className="text-amber-500 fill-amber-500" size={20} />
                <span>Client Consultations & Feedback</span>
              </h2>
              <span className="text-xs font-semibold text-slate-500">
                Verified Consultations
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {testimonials.map((review, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-2xl bg-white border border-amber-900/10 shadow-sm flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1 text-amber-500">
                        {Array.from({ length: review.rating || 5 }).map((_, i) => (
                          <IoStar key={i} size={14} className="fill-amber-500" />
                        ))}
                      </div>
                      {review.concern && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200/50">
                          {review.concern}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-slate-700 italic leading-relaxed mb-4">
                      &ldquo;{review.quote}&rdquo;
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-900">{review.name}</span>
                    {review.city && <span className="text-slate-500">{review.city}</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Bottom Booking Banner */}
        <section className="mt-12 text-center p-8 rounded-3xl bg-white border border-amber-900/10 shadow-md">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-2">
              Ready for Life-Changing Direction?
            </h2>
            <p className="text-sm text-slate-600 mb-6">
              Connect with {expert.name} for a dedicated 1-on-1 private consultation. Accurate analysis, zero judgment, and practical remedies.
            </p>
            <button
              onClick={() => openQuiz("bottom_banner")}
              className="px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-[#be5833] text-white font-bold text-base shadow-lg shadow-orange-500/20 hover:brightness-110 active:scale-95 transition-all inline-flex items-center gap-2 cursor-pointer"
            >
              <IoCalendarOutline size={20} />
              <span>Book Consultation • {formattedPrice}</span>
            </button>
          </div>
        </section>
      </main>

      {/* Mobile-First Sticky Action Bar */}
      <aside aria-label="Book Consultation" className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-amber-900/10 p-3 sm:hidden shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-slate-900 line-clamp-1">{expert.name}</p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-extrabold font-serif text-slate-950">{formattedPrice}</span>
              {formattedOriginalPrice && (
                <span className="text-[11px] text-slate-400 line-through">{formattedOriginalPrice}</span>
              )}
              {discountPercent && (
                <span className="text-[10px] font-bold text-emerald-600">{discountPercent}% OFF</span>
              )}
            </div>
          </div>

          <button
            onClick={() => openQuiz("mobile_sticky")}
            className="flex-shrink-0 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-[#be5833] text-white font-bold text-xs shadow-md active:scale-95 transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <IoCalendarOutline size={16} />
            <span>Book Now</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
